import type { TrackAndFieldMeasurementResponseRecord } from '@on3/api';
import clsx from 'clsx';
import { Text } from 'components/UI/Typography/Text';
import { format } from 'date-fns';
import { useMemo } from 'react';

import styles from './TrackAndField.module.scss';

export enum RecordCode {
  'None' = '',
  'PersonalRecord' = 'PR',
  'StateRecord' = 'SR',
  'WorldRecord' = 'WR',
}
export enum RecordStyles {
  'PersonalRecord' = 'personal',
  'StateRecord' = 'state',
  'WorldRecord' = 'world',
}
interface ITrackAndFieldItem extends TrackAndFieldMeasurementResponseRecord {
  orgLevel?: string | null;
  startDate?: string | null;
}

// Broad Jump, Long Jump, Shot Put
export const keysToConvertToFeet = [8, 15, 21];

export const TrackAndFieldItem = ({
  measurementType,
  measurementTypeKey,
  value,
  eventName,
  record,
  startDate,
  orgLevel,
}: ITrackAndFieldItem) => {
  const formattedDate = startDate && format(new Date(startDate), 'M/d/yyyy');
  const isInchesToFeet =
    measurementTypeKey && keysToConvertToFeet.includes(measurementTypeKey);
  const isTimeFormat = measurementType?.units === 'secs';
  const formattedValue = useMemo(() => {
    if (value) {
      if (isInchesToFeet) {
        const inches = value;
        const feet = Math.floor(inches / 12);
        const remainingInches = inches % 12;

        const formattedInches = remainingInches.toFixed(0);

        return `${feet}'${formattedInches}"`;
      }

      if (isTimeFormat) {
        const minutes = Math.floor(value / 60);
        const remainingSeconds = Math.floor(value % 60);
        const milliseconds = Math.floor((value % 1) * 100);

        let timeString = '';

        if (minutes > 0) {
          timeString += minutes.toString() + ':';
        }

        timeString +=
          remainingSeconds.toString() + '.' + milliseconds.toString();

        return timeString;
      }

      return parseFloat(value.toFixed(2));
    }
  }, [isInchesToFeet, isTimeFormat, value]);

  return (
    <Text className={styles.measurementItem} component="li">
      <span className={styles.measurementName}>{measurementType?.type}</span>
      <div className={styles.columnsContainer}>
        <div className={styles.columnOne}>
          <span className={styles.measurementValue}>{formattedValue}</span>
          {record !== 'None' && (
            <span
              className={clsx(
                styles.record,
                styles[
                  RecordStyles[record as keyof typeof RecordStyles]
                ] as keyof typeof styles,
              )}
            >
              {RecordCode[record as keyof typeof RecordCode]}
            </span>
          )}
        </div>
        <Text variant="caption">{eventName}</Text>
        <div className={styles.columnThree}>
          <span>{orgLevel}</span>
          <span>{formattedDate}</span>
        </div>
      </div>
    </Text>
  );
};
